import { DownloadSimpleIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';

import { BaseButton } from '../../../components/button/BaseButton';
import { Button } from '../../../components/button/Button';
import { exportCreditNotes } from '../models';
import { Formik } from 'components/form/formik';
import { InputField } from '../../../components/input/InputField';
import { getDisplayError } from '../../../utils/get-display-error';
import { downloadTextFile } from '../../../utils/download';
import { parseNumberInput } from '../../../utils/number';
import { captureException } from '@sentry/react';

const exportCreditNotesSchema = Yup.object().shape({
  startId: Yup.string().min(1, 'Vereist').required('Vereist'),
  endId: Yup.string().min(1, 'Vereist').required('Vereist'),
  financialPeriod: Yup.string().min(1, 'Vereist').required('Vereist'),
});

export const ExportCreditNotesButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild disabled={isExporting}>
        <BaseButton color="default" iconLeft={<DownloadSimpleIcon className="button-icon" />} isLoading={isExporting}>
          Exporteer credit notas
        </BaseButton>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />

        <Dialog.Content className="dialog-content">
          <Dialog.Title className="heading-one mb-4">Exporteer credit notas</Dialog.Title>

          <div>
            <Formik
              initialValues={{ startId: '', endId: '', financialPeriod: '1' }}
              validationSchema={exportCreditNotesSchema}
              onSubmit={async ({ startId, endId, financialPeriod }) => {
                setIsExporting(true);
                try {
                  const result = await exportCreditNotes({
                    startId,
                    endId,
                    financialPeriod: parseNumberInput(financialPeriod),
                  });

                  const customers = result.customers;
                  downloadTextFile(customers, `customers-${startId}-${endId}.xml`, 'text/xml');

                  const creditNotes = result.creditNotes;
                  downloadTextFile(creditNotes, `creditNotes-${startId}-${endId}.xml`, 'text/xml');

                  toast.error('Credit nota export download gestart');
                } catch (err) {
                  captureException(err);
                  console.error(err);
                  toast.error('Kon credit notas niet exporteren: ' + getDisplayError(err));
                } finally {
                  setIsExporting(false);
                }
              }}
            >
              {({ handleSubmit, isSubmitting }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <InputField labelText="Start nummer" type="text" name="startId" />
                    <InputField labelText="Eind nummer" type="text" name="endId" />
                    <InputField labelText="Financiele Periode" type="number" name="financialPeriod" />

                    <div className="mt-8">
                      <Button
                        type="submit"
                        color="primary"
                        isDisabled={isSubmitting}
                        isLoading={isSubmitting}
                        iconLeft={<DownloadSimpleIcon className="button-icon" />}
                      >
                        Exporteer credit notas
                      </Button>
                    </div>
                  </form>
                );
              }}
            </Formik>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
